import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Topbar from "../components/Topbar";
import AiReportPanel from "../components/hotels/AiReportPanel";
import { listHotels } from "../api/hotels";
import { useAuth } from "../context/AuthContext";
import { HotelOut } from "../types/hotels";
import { normalizeAiReportDays } from "../utils/aiReports";

export default function AiAssistantPage() {
  const { company_id: companyId } = useAuth();
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-indigo-50/40 text-slate-900">
      <Topbar />
      <main className="mx-auto w-full max-w-[1400px] space-y-8 px-6 py-10">
        <section className="rounded-[2rem] border border-white/80 bg-white/90 p-8 shadow-[0_24px_70px_rgba(79,70,229,0.12)]">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">ИИ-отчёт</h1>
          <p className="mt-4 text-base leading-7 text-slate-600">
            Анализ цен и позиции объекта размещения относительно конкурентов за последние 28 дней.
          </p>
        </section>
        {companyId ? <CompanyAiReports key={companyId} companyId={companyId} /> : (
          <p className="rounded-2xl bg-white p-5 text-slate-500">Выберите компанию для просмотра объектов размещения.</p>
        )}
      </main>
    </div>
  );
}

function CompanyAiReports({ companyId }: { companyId: number }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedGroupId = searchParams.get("group_id");
  const [hotels, setHotels] = useState<HotelOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const controllerRef = useRef<AbortController | null>(null);
  const selectedHotel = hotels.length === 1 ? hotels[0] : hotels.find((hotel) => String(hotel.group_id) === requestedGroupId);
  const singleHotel = !loading && !error && hotels.length === 1;

  const loadHotels = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const items: HotelOut[] = [];
      let page = 1;
      let total = 0;
      do {
        const response = await listHotels(companyId, { ai_enable: true, page, per_page: 200 }, controller.signal);
        if (controller.signal.aborted) return;
        items.push(...response.items);
        total = response.total;
        if (!response.items.length) break;
        page += 1;
      } while (items.length < total);
      setHotels(items);
    } catch (e) {
      if (!controller.signal.aborted) {
        setError("Не удалось загрузить список объектов размещения");
        throw e;
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void loadHotels().catch(() => {});
    return () => controllerRef.current?.abort();
  }, [loadHotels]);

  return (
    <>
      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        {singleHotel ? (
          <>
            <h2 className="text-lg font-semibold">Объект размещения</h2>
            <p className="mt-2 text-base text-slate-600">{selectedHotel?.name}</p>
            {!selectedHotel?.group_id && <p className="mt-2 text-sm text-amber-800">Для объекта не задана группа парсинга.</p>}
          </>
        ) : (
          <>
        <label className="text-lg font-semibold" htmlFor="ai-report-hotel-select">Объект размещения</label>
        <select id="ai-report-hotel-select" value={selectedHotel?.group_id ?? ""}
          onChange={(event) => {
            const next = new URLSearchParams(searchParams);
            if (event.target.value) next.set("group_id", event.target.value);
            else next.delete("group_id");
            setSearchParams(next);
          }}
          disabled={loading || !hotels.length}
          className="mt-4 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base outline-none focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-50">
          <option value="">{loading ? "Загрузка…" : "— Выберите объект —"}</option>
          {hotels.map((hotel) => (
            <option key={hotel.id} value={hotel.group_id ?? `unavailable-${hotel.id}`} disabled={!hotel.group_id}>
              {hotel.name}{!hotel.group_id ? " — группа не задана" : ""}
            </option>
          ))}
        </select>
          </>
        )}
        {error && (
          <div role="alert" className="mt-4 text-sm text-amber-800">
            {error}
            <button type="button" onClick={() => void loadHotels().catch(() => {})} className="ml-3 underline">Повторить загрузку</button>
          </div>
        )}
        {!loading && !error && !hotels.length && <p className="mt-4 text-sm text-slate-500">В выбранной компании нет объектов с включёнными ИИ-отчётами. Обратитесь к администратору.</p>}
        {!loading && !error && requestedGroupId && !selectedHotel && (
          <p className="mt-4 text-sm text-amber-800">Объект размещения недоступен в выбранной компании или для него не включён ИИ-отчёт.</p>
        )}
      </section>
      {!loading && !error && selectedHotel?.group_id && (
        <AiReportPanel key={selectedHotel.group_id} groupId={selectedHotel.group_id} reportDays={normalizeAiReportDays(selectedHotel)} />
      )}
    </>
  );
}
